/*
  Typed calls by endpoint name (the names of the tables in BUILD-PLAN 2.9).

    const health = await runQuery("health", {});
    const card = await runCommand("accept_card", { id: "PC-0001" });

  The route table and the request and response types are generated from the back end
  (`routes.ts`, `types.ts`). Path parameters are taken from the input; the rest is the query string
  (queries) or the JSON body (commands).
*/

import { command, get, getApiSpace, upload, type CommandReply, type Params, type Primitive } from "./client";
import {
  ROUTES,
  type CommandInputs,
  type CommandName,
  type CommandResponses,
  type QueryInputs,
  type QueryName,
  type QueryResponses,
} from "./routes";

/** Fields whose name in the request model differs from the name in the request body. */
const BODY_RENAMES: Record<string, Record<string, string>> = {
  // `name` is the discriminator of the command union, so the element's name is `element_name` in the model.
  add_by_hand: { element_name: "name" },
};

/** Commands whose contract has no `command_id` (2.9.2: sign-in is `{username, password}` and nothing else). */
const WITHOUT_COMMAND_ID = new Set<string>(["sign_in"]);

/**
 * In the practice space the same paths under `/api/practice/...` reach the person's own practice store
 * (2.9, BR-3). These endpoints belong to the person and the discipline, not to the practice store, so they stay
 * on `/api/ws/...`: starting and wiping the practice space, the modules' progress, and the person's memory.
 * One list, so a change in the back end's binding is a change in one place.
 */
export const STAYS_IN_THE_WORKSPACE: ReadonlySet<string> = new Set([
  "start_practice",
  "wipe_practice",
  "onboarding",
  "set_module_status",
  "my_memory",
  "edit_my_memory",
  "clear_my_memory",
]);

/** The address of an endpoint for the space the person works in now. */
export function spacePath(name: string, path: string, space = getApiSpace()): string {
  if (space !== "practice" || STAYS_IN_THE_WORKSPACE.has(name) || !path.startsWith("/api/ws/")) return path;
  return `/api/practice/${path.slice("/api/ws/".length)}`;
}

export type QueryParams<K extends QueryName> = Omit<QueryInputs[K], "name">;
export type CommandInput<K extends CommandName> = Omit<CommandInputs[K], "name" | "command_id">;

const PLACEHOLDER = /\{(\w+)\}/g;

/** Fill the `{param}` placeholders of a path and say which input keys were used. */
export function fillPath(path: string, input: Record<string, unknown>): { path: string; used: Set<string> } {
  const used = new Set<string>();
  const filled = path.replace(PLACEHOLDER, (_all, key: string) => {
    const value = input[key];
    if (value === undefined || value === null) throw new Error(`missing path parameter "${key}" for ${path}`);
    used.add(key);
    return encodeURIComponent(String(value));
  });
  return { path: filled, used };
}

function rest(input: Record<string, unknown>, used: Set<string>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) {
    if (used.has(key) || key === "name" || value === undefined) continue;
    out[key] = value;
  }
  return out;
}

export function routeOf(name: string) {
  const route = ROUTES[name];
  if (!route) throw new Error(`unknown endpoint: ${name}`);
  return route;
}

/** The query key of an endpoint: one per endpoint name, then its parameters. */
export function queryKey<K extends QueryName>(name: K, params?: QueryParams<K>): readonly [K, object] {
  return [name, params ?? {}] as const;
}

export async function runQuery<K extends QueryName>(name: K, params: QueryParams<K>): Promise<QueryResponses[K]> {
  const route = routeOf(name);
  const input = params as Record<string, unknown>;
  const { path, used } = fillPath(route.path, input);
  return get<QueryResponses[K]>(spacePath(name, path), rest(input, used) as Params);
}

export async function runCommand<K extends CommandName>(name: K, input: CommandInput<K> & { command_id?: string }): Promise<CommandReply<CommandResponses[K]>> {
  const route = routeOf(name);
  if (!route.path) throw new Error(`${name} is an agent-only command: it has no REST path`);
  if (route.multipart) throw new Error(`${name} sends a file: use runUpload`);
  const values = input as Record<string, unknown>;
  const { path, used } = fillPath(route.path, values);
  const body = rest(values, used);
  for (const [from, to] of Object.entries(BODY_RENAMES[name] ?? {})) {
    if (from in body) {
      body[to] = body[from];
      delete body[from];
    }
  }
  return command<CommandResponses[K]>(route.method as "POST" | "PUT" | "PATCH" | "DELETE", spacePath(name, path), body, {
    commandId: !WITHOUT_COMMAND_ID.has(name),
  });
}

export async function runUpload<K extends CommandName>(name: K, input: CommandInput<K>, file: File | Blob): Promise<CommandReply<CommandResponses[K]>> {
  const route = routeOf(name);
  if (!route.multipart) throw new Error(`${name} does not send a file: use runCommand`);
  const values = input as Record<string, unknown>;
  const { path, used } = fillPath(route.path, values);
  const form = new FormData();
  for (const [key, value] of Object.entries(rest(values, used))) {
    if (value === undefined || value === null) continue;
    form.set(key, typeof value === "object" ? JSON.stringify(value) : String(value as Primitive));
  }
  form.set("file", file);
  return upload<CommandResponses[K]>(spacePath(name, path), form);
}
